import type {
  BulkDeleteResult,
  BulkRenameItem,
  BulkRenameResult,
} from 'switch-shared/modules/crud/base/base.dto';
import {
  CreateDto,
  UpdateDto,
} from 'switch-shared/modules/crud/base/base.service';
import type {
  ApproveOrRejectJobInput,
  InsertUtilityDataMutation,
  MutationBase,
  RollbackInput,
  ScaleUtilityDataMutation,
  SearchJobsInput,
  SearchJobsOutput,
} from 'switch-shared/modules/crud/job/job.dto';
import { Job } from 'switch-shared/modules/crud/job/job.entity';
import {
  IoTMeterState,
  LinkMeterDto,
  MeterCountByTagOutput,
  MeterCountByTagQuery,
  MeterCountOnlineOutput,
  MeterCountOnlineQuery,
  MeterCountOutput,
  MeterCountQuery,
  MeterCountSummaryOutput,
  MeterCountSummaryQuery,
  SearchMeterInput,
  SearchMeterResultDto,
  ToggleIoTStateInput,
} from 'switch-shared/modules/crud/meter/meter.dto';
import { Meter } from 'switch-shared/modules/crud/meter/meter.entity';
import type { ReadParametersResult } from 'switch-shared/modules/crud/meter/meter.service';
import { ReadingCleanupMode } from 'switch-shared/modules/features/meter-replacement/meter-replacement.dto';
import type { ChangeDeviceDto } from 'switch-shared/modules/features/meter-replacement/meter-replacement.dto';
import type { ChangeDeviceResult } from 'switch-shared/modules/features/meter-replacement/meter-replacement.service';
import type { Parameter } from 'switch-shared/types/parameter.enums';

import { baseAPI, setToast } from './baseAPI.service';
import { API_METHODS, ENDPOINT_URLS } from './config/apiTags';

export const meterAPI = baseAPI
  .enhanceEndpoints({
    addTagTypes: [
      ENDPOINT_URLS.METER,
      `${ENDPOINT_URLS.METER}/jobs`,
      ENDPOINT_URLS.METER_TASK,
    ],
  })
  .injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
      getMeterById: builder.query<Meter, string>({
        query: (id) => `${ENDPOINT_URLS.METER}/${id}`,
        providesTags: (_res, _err, id) => [{ type: ENDPOINT_URLS.METER, id }],
      }),

      getAllMeters: builder.query<SearchMeterResultDto, SearchMeterInput>({
        query: (params) => {
          return {
            url: `${ENDPOINT_URLS.METER}`,
            params,
          };
        },
        providesTags: (res, _err, _) =>
          res?.data.map((con) => ({
            type: ENDPOINT_URLS.METER,
            id: con._id,
          })) ?? [],
      }),

      getMeterIotState: builder.query<IoTMeterState, { eid: string }>({
        query: (params) => {
          return {
            url: `${ENDPOINT_URLS.METER}/${params.eid}/state`,
          };
        },
        providesTags: ['IotState'],
      }),

      getMetersOnlineSummary: builder.query<
        MeterCountOnlineOutput,
        MeterCountOnlineQuery
      >({
        query: (params) => {
          return {
            url: `${ENDPOINT_URLS.METER}/countOnline`,
            params,
          };
        },
      }),

      getMetersSummary: builder.query<
        MeterCountSummaryOutput,
        MeterCountSummaryQuery
      >({
        query: (params) => {
          return {
            url: `${ENDPOINT_URLS.METER}/countSummary`,
            params,
          };
        },
      }),

      getMetersCountByTag: builder.query<
        MeterCountByTagOutput,
        MeterCountByTagQuery
      >({
        query: (params) => {
          return {
            url: `${ENDPOINT_URLS.METER}/countSummaryByTag`,
            params,
          };
        },
      }),

      getMeterCount: builder.query<MeterCountOutput, MeterCountQuery>({
        query: (params) => {
          return {
            url: `${ENDPOINT_URLS.METER}/count`,
            params,
          };
        },
      }),

      getPrePaidMetersOnSummary: builder.query<
        MeterCountOnlineOutput,
        MeterCountOnlineQuery
      >({
        query: (params) => {
          return {
            url: `${ENDPOINT_URLS.METER}/countPrepaidOn`,
            params,
          };
        },
      }),

      createMeter: builder.mutation<Meter, CreateDto<Meter>>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.METER}`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [ENDPOINT_URLS.METER],
      }),

      linkByEidOrMeterNo: builder.mutation<Meter, LinkMeterDto>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.METER}/link`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [ENDPOINT_URLS.METER],
      }),

      replaceDevice: builder.mutation<
        {
          meter: Meter;
          merged: boolean;
          mergedMeterIds: string[];
          oldEid: string;
          deletedReadings: number;
          scaledNewReadings: number;
          offsetReset: boolean;
        },
        {
          id: string;
          newEid: string;
          cleanupMode?: ReadingCleanupMode;
          resetOffset?: boolean;
          applyPulseRate?: boolean;
        }
      >({
        query: ({ id, newEid, cleanupMode, resetOffset, applyPulseRate }) => ({
          url: `${ENDPOINT_URLS.METER}/${id}/change-device`,
          method: API_METHODS.POST,
          body: { newEid, cleanupMode, resetOffset, applyPulseRate },
        }),
        invalidatesTags: (_res, _err, { id }) => [
          { type: ENDPOINT_URLS.METER, id },
        ],
      }),

      toggleIoTState: builder.mutation<number, ToggleIoTStateInput>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.METER}/toggle-iot-state`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: ['IotState'],
      }),

      updateMeter: builder.mutation<
        Meter,
        { id: string; data: UpdateDto<Meter> }
      >({
        query: ({ id, data }) => {
          return {
            url: `${ENDPOINT_URLS.METER}/${id}`,
            method: API_METHODS.PATCH,
            body: data,
          };
        },
        invalidatesTags: (_res, _err, { id }) => [
          {
            type: ENDPOINT_URLS.METER,
            id,
          },
        ],
      }),

      bulkRenameMeters: builder.mutation<BulkRenameResult[], BulkRenameItem[]>({
        query: (items) => ({
          url: `${ENDPOINT_URLS.METER}/bulk-rename`,
          method: API_METHODS.PATCH,
          body: { items },
        }),
        invalidatesTags: (_res, _err, items) =>
          items.map(({ id }) => ({ type: ENDPOINT_URLS.METER, id })),
      }),

      bulkTransferMeter: builder.mutation<
        Meter[],
        { ids: string[]; applicationId: string }
      >({
        query: ({ ids, applicationId }) => ({
          url: `${ENDPOINT_URLS.METER}/bulk-transfer`,
          method: API_METHODS.PATCH,
          body: { ids, applicationId },
        }),
        invalidatesTags: [ENDPOINT_URLS.METER],
      }),

      bulkDeleteMeter: builder.mutation<BulkDeleteResult[], string[]>({
        query: (ids) => ({
          url: `${ENDPOINT_URLS.METER}`,
          method: API_METHODS.DELETE,
          body: ids,
        }),
        invalidatesTags: [ENDPOINT_URLS.METER],
      }),

      unlinkMeterFromGateway: builder.mutation<Meter, string>({
        query: (id) => ({
          url: `${ENDPOINT_URLS.METER}/${id}/unlink-gateway`,
          method: API_METHODS.POST,
        }),
        invalidatesTags: (_res, _err, id) => [
          {
            type: ENDPOINT_URLS.METER,
            id,
          },
        ],
      }),

      linkMeterToGateway: builder.mutation<Meter, { id: string; imei: string }>(
        {
          query: ({ id, imei }) => ({
            url: `${ENDPOINT_URLS.METER}/${id}/link-gateway`,
            method: API_METHODS.POST,
            body: { imei },
          }),
          invalidatesTags: (_res, _err, { id }) => [
            {
              type: ENDPOINT_URLS.METER,
              id,
            },
          ],
        }
      ),

      changeDevice: builder.mutation<
        ChangeDeviceResult,
        { id: string } & ChangeDeviceDto
      >({
        query: ({ id, ...body }) => ({
          url: `${ENDPOINT_URLS.METER}/${id}/change-device`,
          method: API_METHODS.POST,
          body,
        }),
        invalidatesTags: (_res, _err, { id }) => [
          {
            type: ENDPOINT_URLS.METER,
            id,
          },
        ],
      }),

      resetMeter: builder.mutation<
        Meter,
        { id: string; body: { param?: string } }
      >({
        query: ({ id, body }) => {
          return {
            url: `${ENDPOINT_URLS.METER}/${id}/reset`,
            method: API_METHODS.POST,
            body,
          };
        },
        invalidatesTags: [ENDPOINT_URLS.METER],
      }),

      readMeter: builder.mutation<
        ReadParametersResult,
        { id: string; body: { param?: string; params?: Parameter[] } }
      >({
        query: ({ id, body }) => {
          return {
            url: `${ENDPOINT_URLS.METER}/${id}/read`,
            method: API_METHODS.POST,
            body,
          };
        },
        invalidatesTags: [ENDPOINT_URLS.METER, ENDPOINT_URLS.METER_TASK],
      }),

      searchMeterJobs: builder.query<SearchJobsOutput, SearchJobsInput>({
        query: (params) => {
          return {
            url: `${ENDPOINT_URLS.METER}/jobs`,
            params,
          };
        },
        providesTags: (res, _err, _) =>
          res?.data.map((gen) => ({
            type: `${ENDPOINT_URLS.METER}/jobs`,
            id: gen.id,
          })) ?? [],
      }),

      deleteMeterJob: builder.mutation<{ success: boolean }, string>({
        query: (id) => ({
          url: `${ENDPOINT_URLS.METER}/jobs/${id}`,
          method: API_METHODS.DELETE,
        }),
        invalidatesTags: (_res, _err, id) => [
          {
            type: `${ENDPOINT_URLS.METER}/jobs`,
            id,
          },
        ],
      }),

      createDataScalingJob: builder.mutation<Job, ScaleUtilityDataMutation>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.METER}/jobs/util-scaling`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [`${ENDPOINT_URLS.METER}/jobs`],
      }),

      createDataDeletingJob: builder.mutation<Job, MutationBase>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.METER}/jobs/util-deleting`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [`${ENDPOINT_URLS.METER}/jobs`],
      }),
      createDataInsertingJob: builder.mutation<Job, InsertUtilityDataMutation>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.METER}/jobs/util-inserting`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [`${ENDPOINT_URLS.METER}/jobs`],
      }),
      acceptOrDeclineMeterJob: builder.mutation<Job, ApproveOrRejectJobInput>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.METER}/jobs/approveOrReject`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [`${ENDPOINT_URLS.METER}/jobs`],
      }),
      rollbackJob: builder.mutation<Job, RollbackInput>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.METER}/jobs/rollback`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [`${ENDPOINT_URLS.METER}/jobs`],
      }),
    }),
  });

setToast('getMeterIotState', {
  fulfilled: false,
  pending: false,
  error: {
    404: 'No IOT state found for this meter.',
  },
});

setToast('readMeter', {
  fulfilled: 'Attempting to read the parameter. The state will shortly refresh',
});

setToast('resetMeter', {
  pending: false,
  fulfilled:
    'Attempting to reset the parameter. The state will shortly refresh',
});

setToast('replaceDevice', {
  fulfilled: 'The meter device was changed successfully.',
});

setToast('unlinkMeterFromGateway', {
  fulfilled: 'Meter unlinked from gateway',
});

setToast('linkMeterToGateway', {
  fulfilled: 'Meter linked to gateway',
});

export const {
  useGetMeterByIdQuery,
  useGetAllMetersQuery,
  useLazyGetAllMetersQuery,
  useGetMetersOnlineSummaryQuery,
  useGetMetersSummaryQuery,
  useGetMetersCountByTagQuery,
  useGetMeterCountQuery,
  useGetPrePaidMetersOnSummaryQuery,
  useCreateMeterMutation,
  useUpdateMeterMutation,
  useBulkRenameMetersMutation,
  useBulkTransferMeterMutation,
  useBulkDeleteMeterMutation,
  useDeleteMeterJobMutation,
  useGetMeterIotStateQuery,
  useSearchMeterJobsQuery,
  useCreateDataDeletingJobMutation,
  useCreateDataInsertingJobMutation,
  useCreateDataScalingJobMutation,
  useLinkByEidOrMeterNoMutation,
  useUnlinkMeterFromGatewayMutation,
  useLinkMeterToGatewayMutation,
  useReplaceDeviceMutation,
  useToggleIoTStateMutation,
  useResetMeterMutation,
  useReadMeterMutation,
  useAcceptOrDeclineMeterJobMutation,
  useRollbackJobMutation,
} = meterAPI;
export default meterAPI;

setToast('bulkDeleteMeter', { bulkResults: { noun: 'meter', verb: 'delete' } });
setToast('bulkRenameMeters', {
  bulkResults: { noun: 'meter', verb: 'rename' },
});
